/* Product behavior shared by the browser and regression tests. No network side effects. */
(function (root) {
  'use strict';
  const VERSION = 'growth-v2';
  const FLAGS = Object.freeze({ onboarding: true, circles: true, weekly: true });
  const DAY = 86400000;
  const validKey = key => typeof key === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(key) && Number.isFinite(Date.parse(key)) && new Date(key).toISOString().slice(0, 10) === key;
  const shift = (key, days) => new Date(Date.parse(key) + days * DAY).toISOString().slice(0, 10);
  function weekKeys(key) {
    if (!validKey(key)) return [];
    const monday = shift(key, -((new Date(key).getUTCDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => shift(monday, i));
  }
  function cleanLedger(raw, today) {
    const result = {};
    if (!validKey(today) || !raw || typeof raw !== 'object') return result;
    const earliest = shift(today, -55);
    for (const [key, value] of Object.entries(raw)) {
      if (validKey(key) && key >= earliest && key <= today && (value === 'played' || value === 'solved')) result[key] = value;
    }
    return result;
  }
  function weekly(raw, history, today) {
    const ledger = cleanLedger(raw, today);
    for (const row of history || []) {
      if (validKey(row.dailyKey) && row.dailyKey <= today) ledger[row.dailyKey] = 'solved';
    }
    const days = weekKeys(today).map(key => ({ key, status: key > today ? 'future' : ledger[key] || 'unknown' }));
    return { days, played: days.filter(d => ['played', 'solved'].includes(d.status)).length, solved: days.filter(d => d.status === 'solved').length };
  }
  function rotate(board, index, clockwise = true) {
    const next = board.slice(), r = Math.floor(index / 5), c = index % 5;
    const a = r * 6 + c, b = a + 1, d = a + 6, e = d + 1;
    if (clockwise) [next[a], next[b], next[e], next[d]] = [board[d], board[a], board[b], board[e]];
    else [next[a], next[b], next[e], next[d]] = [board[b], board[e], board[d], board[a]];
    return next;
  }
  const SOLVED_DEMO = 'GRLNKWMPLAYTDSHRBF'.split('');
  const DEMO_MOVES = [1, 3];
  const DEMO = rotate(rotate(SOLVED_DEMO, 3, false), 1, false);
  const demoSolved = board => board.slice(7, 11).join('') === 'PLAY';
  function attribution(search) {
    const p = new URLSearchParams(search);
    // Fixed campaign vocabulary prevents arbitrary identifying strings entering telemetry.
    const allowed = { source: ['owned', 'community', 'groupchat'], medium: ['video', 'image', 'text'], campaign: ['launch-v2'], creative: ['mechanic', 'last-move', 'together'] };
    const out = {};
    for (const [key, values] of Object.entries(allowed)) if (values.includes(p.get('utm_' + key))) out[key] = p.get('utm_' + key);
    out.referral_kind = p.has('circle') ? 'circle' : p.has('cid') || p.has('challenge') ? 'challenge' : p.get('ref') === 'friend' ? 'friend' : out.campaign ? 'campaign' : 'direct';
    return out;
  }
  function followupEligible(record, now) {
    return (!record || Number(record.count || 0) < 2) && (!record?.at || now - Number(record.at) >= 7 * DAY);
  }
  function create(api) {
    let demo = null, continuation = null, shown = false, busyUntil = 0, postSolve = false, lastInteraction = 0, followup = null, cardAttempted = false;
    let returnFocus = null, demoTimer = null;
    const seen = new Set();
    const read = (key, fallback) => { try { return JSON.parse(api.get(key) || 'null') ?? fallback; } catch { return fallback; } };
    let ledger = cleanLedger(read('pivot_weekly_activity_v1', {}), api.today());
    const sessionTouch = attribution(location.search);
    const storedTouch = read('pivot_growth_first_touch_v2', null);
    const firstTouch = storedTouch && Number.isFinite(storedTouch.at) && storedTouch.at <= Date.now() && Date.now() - storedTouch.at < 30 * DAY ? { at: storedTouch.at, ...attribution(new URLSearchParams(Object.entries(storedTouch).filter(([key]) => ['source','medium','campaign','creative'].includes(key)).map(([key,value]) => ['utm_'+key,value])).toString()), referral_kind: ['circle','challenge','friend','campaign','direct'].includes(storedTouch.referral_kind) ? storedTouch.referral_kind : 'direct' } : { at: Date.now(), ...sessionTouch };
    api.set('pivot_growth_first_touch_v2', JSON.stringify(firstTouch));
    const id = () => root.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    function event(name, extra = {}, once = '') {
      if (once && seen.has(name + once)) return;
      if (once) seen.add(name + once);
      api.log(name, { schema_version: 2, event_id: id(), build_version: api.build(), surface: api.state().mode, gameplay_context: demo ? 'tutorial' : api.state().customMode ? 'custom' : api.state().practiceMode ? 'practice' : api.state().redoMode ? 'redo' : 'daily', growth_version: VERSION, ...sessionTouch, first_touch: firstTouch, ...extra });
    }
    function activity(solved = false) {
      cachedWeek = null; historyAt = 0;
      const s = api.state();
      if (s.customMode || s.practiceMode || s.redoMode || s.dailyKey !== api.today()) return;
      ledger = cleanLedger(ledger, api.today());
      ledger[s.dailyKey] = solved || ledger[s.dailyKey] === 'solved' ? 'solved' : 'played';
      api.set('pivot_weekly_activity_v1', JSON.stringify(ledger));
      if (!solved) {
        event('daily_active', {}, s.dailyKey);
        if (s.moves === 1) event('daily_first_rotation', {}, s.dailyKey);
      }
    }
    let cachedWeek = null, weekAt = 0, cachedHistory = [], historyAt = 0;
    function history() { if (!historyAt || Date.now()-historyAt>1000) { cachedHistory=api.history(); historyAt=Date.now(); } return cachedHistory; }
    function week() { if (!cachedWeek || Date.now() - weekAt > 1000) { cachedWeek = weekly(ledger, history(), api.today()); weekAt = Date.now(); } return cachedWeek; }
    function weeklyHtml() {
      if (!FLAGS.weekly) return '';
      const w = week();
      return `<section class="growth-week" aria-label="Weekly activity"><strong>Played ${w.played} days this week</strong><div class="growth-days">${w.days.map((d, i) => `<span class="growth-day ${d.status}" aria-label="${d.key}: ${d.status === 'unknown' ? 'no activity recorded' : d.status}"><span>${['M','T','W','T','F','S','S'][i]}</span><span aria-hidden="true">${d.status === 'solved' ? '●' : d.status === 'played' ? '○' : '·'}</span></span>`).join('')}</div><small>Week of ${w.days[0]?.key || ''} · Eastern<br>○ Played · ● Solved · History known on this device</small></section>`;
    }
    function personalLine() {
      return api.state().dailyKey === api.today() ? 'Next PIVOT: tomorrow at midnight ET' : 'A new daily PIVOT is ready';
    }
    function button(text, action, primary = false) {
      const b = document.createElement('button'); b.type = 'button'; b.textContent = text; b.className = primary ? 'growth-primary' : ''; b.onclick = action; return b;
    }
    const entry = button("Play today's puzzle", () => api.start(), true);
    entry.id = 'growth-play'; entry.hidden = true; document.body.append(entry);
    function positionEntry(rect) {
      const s = api.state(), blocked = api.blocked();
      entry.hidden = s.mode !== 'menu' || s.customMode || !!demo || blocked;
      if (entry.hidden) return;
      const box = api.canvas().getBoundingClientRect();
      const scaleX = box.width / api.size().width, scaleY = box.height / api.size().height;
      Object.assign(entry.style, { left: `${box.left + rect.x * scaleX}px`, top: `${box.top + rect.y * scaleY}px`, width: `${rect.width * scaleX}px`, height: `${Math.max(44, rect.height * scaleY)}px` });
      entry.textContent = api.entryLabel();
      entry.disabled = api.loading();
      event('landing_view', { entry_state: entry.textContent, new_device: api.newDevice() ? 1 : 0 }, 'landing');
      if (sessionTouch.referral_kind !== 'direct') event('referral_landing', {}, 'landing');
    }
    function finishDemo(outcome) {
      if (!demo) return;
      event('onboarding_finished', { outcome, duration_ms: Date.now() - demo.startedAt, attempt_id: demo.id });
      api.set('pivot_onboarding_v2', JSON.stringify({ version: 2, status: outcome }));
      api.markTutorialSeen();
      demo = null; clearTimeout(demoTimer);
      document.getElementById('growth-demo')?.remove();
      returnFocus?.focus?.();
      const next = continuation; continuation = null; next?.(); api.render();
    }
    function renderDemo() {
      entry.hidden = true;
      let overlay = document.getElementById('growth-demo');
      if (!overlay) {
        overlay = document.createElement('div'); overlay.id = 'growth-demo'; overlay.className = 'growth-overlay'; overlay.setAttribute('role', 'dialog'); overlay.setAttribute('aria-modal', 'true'); overlay.setAttribute('aria-labelledby', 'growth-demo-title');
        document.body.append(overlay);
        overlay.addEventListener('keydown', e => {
          if (e.key === 'Escape') { e.preventDefault(); finishDemo('skipped'); }
          if (e.key === 'Tab') {
            const nodes = [...overlay.querySelectorAll('button:not(:disabled)')];
            if (e.shiftKey && document.activeElement === nodes[0]) { e.preventDefault(); nodes.at(-1)?.focus(); }
            else if (!e.shiftKey && document.activeElement === nodes.at(-1)) { e.preventDefault(); nodes[0]?.focus(); }
          }
          e.stopPropagation();
        });
      }
      const focused = document.activeElement?.dataset?.pivot;
      overlay.replaceChildren();
      const card = document.createElement('section'); card.className = 'growth-card'; overlay.append(card);
      const title = document.createElement('h2'); title.id = 'growth-demo-title'; title.textContent = demo.stage === 'done' ? "You've got it!" : 'Learn in two moves'; card.append(title);
      const text = document.createElement('p'); text.setAttribute('aria-live','polite'); text.textContent = demo.stage === 'intro' ? 'Rotate four tiles around a dot. Put PLAY in the gold row.' : demo.stage === 'first' ? 'Tap this dot to rotate these four tiles.' : demo.stage === 'done' ? 'Ready for your daily puzzle?' : 'Put PLAY in the gold row. Every tap rotates four tiles.'; card.append(text);
      const grid = document.createElement('div'); grid.className = 'growth-demo-grid'; grid.setAttribute('aria-label', 'Practice board. Target PLAY. Center: ' + demo.board.slice(7,11).join('')); card.append(grid);
      const selected = demo.stage === 'first' ? 1 : demo.showHint ? 3 : -1;
      const selectedCells = selected < 0 ? [] : [Math.floor(selected / 5) * 6 + selected % 5, Math.floor(selected / 5) * 6 + selected % 5 + 1, Math.floor(selected / 5) * 6 + selected % 5 + 6, Math.floor(selected / 5) * 6 + selected % 5 + 7];
      demo.board.forEach((letter, i) => { const tile = document.createElement('span'); tile.textContent = letter; tile.className = `growth-tile${i >= 7 && i <= 10 ? ' gold' : ''}${selectedCells.includes(i) ? ' selected' : ''}`; grid.append(tile); });
      for (let i = 0; i < 10; i++) {
        const b = button('↻', () => {
          if (!demo || demo.stage === 'intro' || demo.stage === 'done') return;
          demo.board = rotate(demo.board, i); demo.moves++;
          event('onboarding_step', { attempt_id: demo.id, step: demo.moves, correct: i === (demo.stage === 'first' ? 1 : 3) });
          demo.stage = demoSolved(demo.board) ? 'done' : 'second'; demo.showHint = false;
          renderDemo();
        });
        b.dataset.pivot = String(i); b.className = 'growth-pivot' + (selected === i ? ' highlighted' : ''); b.setAttribute('aria-label', `Rotate four tiles at row ${Math.floor(i / 5) + 1}, column ${i % 5 + 1}`);
        b.style.left = `${((i % 5) + 1) / 6 * 100}%`; b.style.top = `${(Math.floor(i / 5) + 1) / 3 * 100}%`;
        b.disabled = demo.stage === 'intro' || demo.stage === 'done' || (demo.stage === 'first' && i !== 1); grid.append(b);
      }
      const actions = document.createElement('div'); actions.className = 'growth-actions'; card.append(actions);
      if (demo.stage === 'intro') actions.append(button('Try it', () => { demo.stage = 'first'; event('onboarding_started', { attempt_id: demo.id }); renderDemo(); }, true));
      else if (demo.stage === 'done') actions.append(button(continuation ? (api.state().challengeFrom ? 'Play this challenge' : "Play today's puzzle") : 'Back to puzzle', () => finishDemo('completed'), true));
      else if (demo.stage === 'second') {
        actions.append(button('Reset demo', () => { demo.board = DEMO.slice(); demo.stage = 'first'; demo.moves = 0; renderDemo(); }));
        if (demo.moves >= 3 || demo.helpReady) actions.append(button('Show the next move', () => { demo.board = rotate(DEMO, 1); demo.showHint = true; renderDemo(); }));
      }
      if (demo.stage !== 'done') actions.append(button(continuation ? 'Skip to puzzle' : 'Close demo', () => finishDemo('skipped')));
      if (focused !== undefined && grid.querySelector(`[data-pivot="${focused}"]:not(:disabled)`)) grid.querySelector(`[data-pivot="${focused}"]`).focus();
      else (grid.querySelector('.highlighted:not(:disabled)') || actions.querySelector('button'))?.focus();
    }
    function showDemo(next = null) {
      if (demo) return true;
      returnFocus = document.activeElement; continuation = next;
      demo = { stage: 'intro', board: DEMO.slice(), moves: 0, startedAt: Date.now(), id: id(), helpReady: false };
      renderDemo();
      demoTimer = setTimeout(() => { if (demo && !document.hidden) { demo.helpReady = true; if (demo.stage === 'second') renderDemo(); } }, 8000);
      return true;
    }
    function maybeOnboard(next) {
      if (!FLAGS.onboarding || api.hasSeenTutorial() || read('pivot_onboarding_v2', null) || api.saved() || api.history().length) return false;
      return showDemo(next);
    }
    function closeFollowup() { followup?.remove(); followup = null; }
    function claimFollowup() { if (shown) return false; shown = true; document.getElementById('updates-banner')?.remove(); return true; }
    async function offerFollowup() {
      if (cardAttempted || shown || !postSolve || api.blocked() || Date.now() < busyUntil || document.hidden) return;
      cardAttempted = true;
      // Circle management stays in Play Together. The result itself carries
      // the one-friend invitation; only an eligible return reminder may follow.
      api.remind();
    }
    const resultActions = new Map();
    function positionResultAction(kind, label, rect) {
      let control = resultActions.get(kind);
      if (!control) {
        control = button('', () => kind === 'friend' ? api.shareFriend() : kind === 'stats' ? api.openStats() : api.openCircleStandings());
        control.className = 'growth-canvas-action'; document.body.append(control); resultActions.set(kind, control);
      }
      control.setAttribute('aria-label',label);
      const box=api.canvas().getBoundingClientRect(), sx=box.width/api.size().width, sy=box.height/api.size().height;
      Object.assign(control.style,{left:`${box.left+rect.x*sx}px`,top:`${box.top+rect.y*sy}px`,width:`${rect.width*sx}px`,height:`${rect.height*sy}px`});
      control.hidden=api.blocked() || document.hidden;
    }
    function tick() {
      if (document.hidden) return;
      const s = api.state();
      for (const [kind, control] of resultActions) if (s.mode !== "win" || s.practiceMode || s.redoMode || api.blocked() || (kind !== "friend" && api.size().height < 560) || (kind === "circle" && (!api.circles().length || api.circleError()))) control.hidden = true;
      entry.hidden = entry.hidden || s.mode !== 'menu' || !!demo || api.blocked();
      if (s.mode !== 'win' || s.customMode || s.practiceMode || s.redoMode) { closeFollowup(); postSolve = false; return; }
      if (api.blocked()) { if (followup) closeFollowup(); lastInteraction = Date.now(); return; }
      if (Date.now() - lastInteraction >= 2000) offerFollowup();
    }
    document.addEventListener('pointerdown', () => { lastInteraction = Date.now(); }, true);
    document.addEventListener('keydown', () => { lastInteraction = Date.now(); }, true);
    setInterval(tick, 500);
    function solved() { activity(true); postSolve = true; cardAttempted = false; lastInteraction = Date.now() + 3500; }
    function shareIntent(kind) { busyUntil = Date.now() + 60000; closeFollowup(); const interaction = id(); event('share_intent', { kind, interaction_id: interaction }); return interaction; }
    function shareOutcome(interaction, outcome) { busyUntil = 0; lastInteraction = Date.now(); event('share_outcome', { interaction_id: interaction, outcome }); }
    return { hideResultAction: kind => { const control=resultActions.get(kind); if (control) control.hidden=true; }, positionResultAction, event, activity, week, weeklyHtml, personalLine, positionEntry, maybeOnboard, showDemo, solved, claimFollowup, followupShown: () => shown, blocked: () => !!demo, state: () => demo ? { stage: demo.stage, grid: demo.board.join(''), moves: demo.moves } : null, shareIntent, shareOutcome, busy: () => Date.now() < busyUntil, resultView: () => { if (api.state().customMode || api.state().practiceMode || api.state().redoMode) return; const key=api.state().dailyKey; if (!seen.has('result_view'+key)) api.loadCircles(); event('result_view', { restored: !postSolve }, key); } };
  }
  function drawShareCard(canvas, { edition, moves, portrait = false, title = 'PIVOT', competitive = false } = {}) {
    canvas.width = 1080; canvas.height = portrait ? 1350 : 1080;
    const ctx = canvas.getContext('2d'), h = canvas.height;
    ctx.fillStyle = '#f7f2e6'; ctx.fillRect(0,0,1080,h);
    ctx.strokeStyle = '#ae8c35'; ctx.lineWidth = 3; ctx.strokeRect(30,30,1020,h-60);
    ctx.textAlign = 'center'; ctx.fillStyle = '#28261f'; ctx.font = 'bold 76px Georgia'; ctx.fillText(title,540,155);
    ctx.font = '30px Georgia'; ctx.fillText(`Edition No. ${edition}`,540,218);
    ctx.font = 'italic 74px Georgia'; ctx.fillText(`${moves} moves`,540,335);
    // This fixed decorative pattern cannot reveal a daily word or solution.
    const pattern = ['P','I','V','O','T','·','·','↻','·','↻','·','·','·','·','·','·','·','·'];
    const size = 126, left = (1080 - size * 6) / 2, top = 420;
    for (let i = 0; i < 18; i++) {
      const x = left + (i % 6) * size, y = top + Math.floor(i / 6) * size;
      ctx.fillStyle = i >= 7 && i <= 10 ? '#d4b65c' : '#f7f2e6'; ctx.fillRect(x,y,size,size);
      ctx.strokeStyle = '#514b3e'; ctx.lineWidth = 2; ctx.strokeRect(x,y,size,size);
      ctx.fillStyle = '#28261f'; ctx.font = 'bold 56px Georgia'; ctx.fillText(pattern[i],x+size/2,y+82);
    }
    ctx.font = '34px Georgia'; ctx.fillText(competitive ? 'Can you beat that?' : 'Rotate four tiles. Spell the word.',540,h-175);
    ctx.font = 'bold 32px Georgia'; ctx.fillText('Play free at pivotthegame.com',540,h-105);
    return canvas;
  }

  const exports = { drawShareCard, VERSION, FLAGS, validKey, shift, weekKeys, cleanLedger, weekly, rotate, DEMO, DEMO_MOVES, demoSolved, attribution, followupEligible, create };
  if (typeof module !== 'undefined') module.exports = exports;
  root.PivotGrowth = exports;
})(typeof window !== 'undefined' ? window : globalThis);
